import React from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Camera, ArrowLeft, Check, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { photographers as photographersApi, bookings as bookingsApi } from '../lib/api';
import { formatINR } from '../lib/auth';

const PLACEHOLDER_AVATAR = 'https://images.unsplash.com/photo-1519741497674-611481863552?w=200&h=200&fit=crop';

const buildOptions = (photographer) => {
  const services = photographer?.services || [];
  return services.flatMap((service) =>
    service.packages?.length
      ? service.packages.map((pkg) => ({
          key: `${service.id}-${pkg.id}`,
          name: `${service.title} — ${pkg.name}`,
          price: Number(pkg.price),
          duration: service.duration_minutes ? `${Math.round(service.duration_minutes / 60)} hours` : service.delivery_days ? `Delivered in ${service.delivery_days} days` : '',
          features: pkg.features || [],
          serviceId: service.id,
          packageId: pkg.id,
          category: service.category,
        }))
      : [
          {
            key: `${service.id}`,
            name: service.title,
            price: Number(service.price),
            duration: service.duration_minutes ? `${Math.round(service.duration_minutes / 60)} hours` : service.delivery_days ? `Delivered in ${service.delivery_days} days` : '',
            features: service.deliverables || [],
            serviceId: service.id,
            packageId: null,
            category: service.category,
          },
        ]
  );
};

const BookingFlow = () => {
  const { photographerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');
  const preselectedPackage = searchParams.get('package');

  const [photographer, setPhotographer] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  const [step, setStep] = React.useState(1); // 1: Package, 2: Date, 3: Details, 4: Payment
  const [selectedKey, setSelectedKey] = React.useState(null);
  const [eventDate, setEventDate] = React.useState('');
  const [eventTime, setEventTime] = React.useState('12:00');
  const [location, setLocation] = React.useState('');
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    photographersApi
      .get(photographerId)
      .then(({ data }) => {
        if (cancelled) return;
        setPhotographer(data);
        const options = buildOptions(data);
        const preselected = options.find(
          (o) => String(o.serviceId) === preselectedService && (preselectedPackage ? String(o.packageId) === preselectedPackage : true)
        );
        if (preselected) setSelectedKey(preselected.key);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load this photographer.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photographerId]);

  const options = React.useMemo(() => buildOptions(photographer), [photographer]);
  const selectedOption = options.find((o) => o.key === selectedKey);

  const handleNext = () => {
    if (step === 1 && !selectedOption) {
      toast.error('Please select a package to continue.');
      return;
    }
    if (step === 2 && !eventDate) {
      toast.error('Please select an event date.');
      return;
    }
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleConfirm = async () => {
    if (!selectedOption || !eventDate) {
      toast.error('Missing package or date - please go back and complete each step.');
      return;
    }
    setSubmitting(true);
    try {
      const eventDateTime = `${eventDate}T${eventTime || '12:00'}:00`;
      const { data: booking } = await bookingsApi.create({
        photographer_id: Number(photographerId),
        service_id: selectedOption.serviceId,
        package_id: selectedOption.packageId,
        event_type: selectedOption.category,
        event_date: eventDateTime,
        location,
        total_amount: selectedOption.price,
        notes,
      });
      // Mock payment - see BookingController::pay. No real card is charged.
      await bookingsApi.pay(booking.id);
      toast.success('Booking confirmed and paid!');
      navigate('/client/dashboard');
    } catch (err) {
      const message = err.response?.data?.message || Object.values(err.response?.data?.errors || {})[0]?.[0] || 'Could not complete this booking.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (!photographer) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">This photographer could not be found.</p>
        <Link to="/photographers"><Button variant="outline">Back to photographers</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link
          to={`/photographer/${photographerId}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Link>

        {/* Progress Steps */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-between" data-testid="progress-steps">
            {[{ num: 1, label: 'Package' }, { num: 2, label: 'Date' }, { num: 3, label: 'Details' }, { num: 4, label: 'Payment' }].map((s) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= s.num ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                    data-testid={`step-${s.num}`}
                  >
                    {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                  </div>
                  <div className="text-xs mt-2 font-medium" data-testid={`step-label-${s.num}`}>{s.label}</div>
                </div>
                {s.num < 4 && <div className={`flex-1 h-0.5 mx-4 ${step > s.num ? 'bg-primary' : 'bg-muted'}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Package Selection */}
            {step === 1 && (
              <div data-testid="step-package">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Choose Your Package</h2>
                {options.length === 0 && <p className="text-muted-foreground">This photographer has no bookable services yet.</p>}
                <div className="space-y-4">
                  {options.map((pkg) => (
                    <Card
                      key={pkg.key}
                      className={`cursor-pointer transition-all ${selectedKey === pkg.key ? 'border-primary shadow-lg' : 'hover:shadow-md'}`}
                      onClick={() => setSelectedKey(pkg.key)}
                      data-testid={`package-option-${pkg.key}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-playfair text-2xl font-semibold mb-1">{pkg.name}</h3>
                            <div className="text-sm text-muted-foreground mb-4">{pkg.duration}</div>
                            <ul className="space-y-2">
                              {pkg.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start text-sm">
                                  <Check className="w-4 h-4 mr-2 text-primary flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="font-playfair text-3xl font-bold whitespace-nowrap">{formatINR(pkg.price)}</div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Date Selection */}
            {step === 2 && (
              <div data-testid="step-date">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Select Your Date</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Event Date</label>
                        <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} data-testid="date-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Event Time</label>
                        <Input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} data-testid="time-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Event Location</label>
                        <Input
                          placeholder="e.g. Salt Lake, Kolkata"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          data-testid="location-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Notes */}
            {step === 3 && (
              <div data-testid="step-details">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Special Requests</h2>
                <Card>
                  <CardContent className="p-6">
                    <label className="text-sm font-medium mb-2 block">Anything the photographer should know? (Optional)</label>
                    <Textarea
                      className="min-h-[120px]"
                      placeholder="Any specific requirements or preferences..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      data-testid="notes-input"
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && (
              <div data-testid="step-payment">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Payment</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="bg-muted/30 p-4 rounded-sm mb-6">
                      <div className="flex items-center justify-center text-yellow-600 mb-2">
                        <CreditCard className="w-5 h-5 mr-2" />
                        <span className="font-semibold">Mock Payment Mode</span>
                      </div>
                      <p className="text-sm text-center text-muted-foreground">
                        This MVP doesn't charge a real card yet. Confirming will mark this booking as paid.
                      </p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between"><span>Package</span><span className="font-medium">{selectedOption?.name}</span></div>
                      <div className="flex items-center justify-between"><span>Event date</span><span className="font-medium">{eventDate} {eventTime}</span></div>
                      <div className="flex items-center justify-between"><span>Location</span><span className="font-medium">{location || '—'}</span></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8" data-testid="navigation-buttons">
              <Button variant="outline" onClick={handleBack} disabled={step === 1 || submitting} data-testid="back-btn">
                Back
              </Button>
              {step < 4 ? (
                <Button onClick={handleNext} disabled={step === 1 && !selectedOption} data-testid="next-btn">
                  Next
                </Button>
              ) : (
                <Button size="lg" onClick={handleConfirm} disabled={submitting} data-testid="confirm-btn">
                  {submitting ? 'Confirming…' : 'Confirm & Pay'}
                </Button>
              )}
            </div>
          </div>

          {/* Booking Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24" data-testid="booking-summary">
              <CardHeader>
                <CardTitle>Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3 pb-4 border-b">
                  <img src={photographer.user?.avatar_path || PLACEHOLDER_AVATAR} alt={photographer.business_name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold" data-testid="summary-photographer">{photographer.business_name || photographer.user?.name}</div>
                    <div className="text-sm text-muted-foreground capitalize">{(photographer.specialties || []).join(', ')}</div>
                  </div>
                </div>

                {selectedOption && (
                  <>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Package</div>
                      <div className="font-semibold" data-testid="summary-package">{selectedOption.name}</div>
                    </div>
                    {eventDate && (
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Date</div>
                        <div className="font-semibold" data-testid="summary-date">{eventDate} {eventTime}</div>
                      </div>
                    )}
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <span>Package Price</span>
                        <span data-testid="summary-subtotal">{formatINR(selectedOption.price)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                        <span>Service Fee</span>
                        <span data-testid="summary-fee">{formatINR(0)}</span>
                      </div>
                      <div className="flex items-center justify-between font-playfair text-2xl font-bold pt-4 border-t">
                        <span>Total</span>
                        <span data-testid="summary-total">{formatINR(selectedOption.price)}</span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;
